import * as React from 'react';
import { Layout, Space } from 'antd';
import ViewWrapper from '../../components/ViewWrapper/ViewWrapper';
import MailSidebar from './components/MailSidebar';
import MailList from './components/MailList';
import MailPreview from './components/MailPreview';
import MailToolbar from './components/MailToolbar';
import { useDispatch, useSelector } from 'react-redux';
import mailService from '../../services/mailService';
import type { Email } from '../../services/mailService';
import './index.scss';

const { Sider, Content } = Layout;

const Mails: React.FC = () => {
    const [selectedFolder, setSelectedFolder] = React.useState('inbox');
    const [selectedEmail, setSelectedEmail] = React.useState<Email | null>(null);
    const [emails, setEmails] = React.useState<Email[]>([]);
    const [allEmails, setAllEmails] = React.useState<Email[]>([]); // For counting purposes
    const [loading, setLoading] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const dispatch = useDispatch();
    const { user } = useSelector((state: any) => state.app);

    // Fetch all emails for counting purposes on mount
    React.useEffect(() => {
        fetchAllEmailsForCounts();
    }, []);

    React.useEffect(() => {
        fetchEmails();
    }, [selectedFolder]);

    const fetchAllEmailsForCounts = async () => {
        try {
            const responseData = await dispatch(mailService.getAllEmails(true, user?._id));
            if (responseData?.data) {
                setAllEmails(responseData.data);
            }
        } catch (error) {
            console.error('Error fetching all emails for counts:', error);
        }
    };

    const fetchEmails = async () => {
        setLoading(true);
        try {
            const responseData = await dispatch(mailService.getEmailsByFolder(selectedFolder, true, user?._id));
            if (responseData?.data) {
                let fetchedEmails = responseData.data;

                // Apply frontend filtering for inbox (emails that are not drafts) only if not using mail service
                if (selectedFolder === 'inbox' && !process.env.MAIL_SERVICE) {
                    fetchedEmails = fetchedEmails.filter(
                        (email: Email) => email.mailStatus !== 'Draft' && email.status !== 'deleted'
                    );
                }

                setEmails(fetchedEmails);
            }
        } catch (error) {
            console.error('Error fetching emails:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEmailSelect = (email: Email) => {
        setSelectedEmail(email);
    };

    const handleFolderChange = (folder: string) => {
        setSelectedFolder(folder);
        setSelectedEmail(null);
    };

    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    const filteredEmails = React.useMemo(() => {
        if (!searchQuery) return emails;
        const query = searchQuery.toLowerCase();
        return emails.filter(
            (email) =>
                email.from?.toLowerCase().includes(query) ||
                email.subject?.toLowerCase().includes(query) ||
                email.mailContent?.subject?.toLowerCase().includes(query) ||
                email.mailContent?.body?.toLowerCase().includes(query)
        );
    }, [emails, searchQuery]);

    return (
        <ViewWrapper
            {...{
                requireAuth: true,
                title: 'Mails',
                isNotRedirected: true,
            }}
        >
            <div className="mails-container">
                <MailToolbar onSearch={handleSearch} />
                <Layout className="mails-layout">
                    <Sider width={250} className="mails-sidebar" theme="light">
                        <MailSidebar
                            selectedFolder={selectedFolder}
                            onFolderChange={handleFolderChange}
                            unreadCounts={{
                                inbox: allEmails.filter((e) => e.mailStatus !== 'Draft' && e.status !== 'deleted').length,
                                drafts: allEmails.filter((e) => e.mailStatus === 'Draft').length,
                            }}
                        />
                    </Sider>
                    <Content className="mails-content">
                        <Layout className="mails-inner-layout">
                            <Content className="mails-list-container">
                                <MailList
                                    emails={filteredEmails}
                                    selectedEmail={selectedEmail}
                                    onEmailSelect={handleEmailSelect}
                                    loading={loading}
                                    folder={selectedFolder}
                                />
                            </Content>
                            <Sider width={600} className="mails-preview-sidebar" theme="light">
                                <MailPreview email={selectedEmail} />
                            </Sider>
                        </Layout>
                    </Content>
                </Layout>
            </div>
        </ViewWrapper>
    );
};

export default Mails;
